import { z } from 'zod';

// Publishing layout options for visualizations
export const visualizationLayoutOptionsDTOSchema = z.object({
  title: z.boolean(),
  logo: z.boolean(),
  publishDate: z.boolean(),
  legend: z.boolean(),
});

export type VisualizationLayoutOptionsDTO = z.infer<typeof visualizationLayoutOptionsDTOSchema>;

export const visualizationAutoPublishOptions = [
  'never',
  'hourly',
  'daily',
  'weekly',
  'monthly',
  'realtime',
] as const;
export const visualizationAutoPublishSchema = z.enum(visualizationAutoPublishOptions);

export type VisualizationAutoPublish = z.infer<typeof visualizationAutoPublishSchema>;

export const visualizationPublishTypeOptions = ['live', 'locked', 'image'] as const;
export const visualizationPublishTypeSchema = z.enum(visualizationPublishTypeOptions);

export type VisualizationPublishType = z.infer<typeof visualizationPublishTypeSchema>;

export const visualizationWeekDayOptions = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
] as const;
export const visualizationWeekDaySchema = z.enum(visualizationWeekDayOptions);

export type VisualizationWeekDay = z.infer<typeof visualizationWeekDaySchema>;

export const visualizationMonthWeekOptions = ['1st', '2nd', '3rd', '4th'] as const;
export const visualizationMonthWeekSchema = z.enum(visualizationMonthWeekOptions);

export type VisualizationMonthWeek = z.infer<typeof visualizationMonthWeekSchema>;

// Authentication type discriminator
export const visualizationAuthTypeOptions = ['none', 'password'] as const;
export const visualizationAuthTypeSchema = z.enum(visualizationAuthTypeOptions);

export type VisualizationAuthType = z.infer<typeof visualizationAuthTypeSchema>;

// Config as stored in database (internal use only - includes sensitive fields)
export const visualizationPublishConfigDTOSchema = z.object({
  layoutOptions: visualizationLayoutOptionsDTOSchema,
  visionStatement: z.string().max(10000).optional(),
  tempoBranding: z.boolean(),
  autoPublish: visualizationAutoPublishSchema,
  weeklyPublishDay: visualizationWeekDaySchema.optional(),
  monthlyPublishWeek: visualizationMonthWeekSchema.optional(),
  monthlyPublishDay: visualizationWeekDaySchema.optional(),
  // Auth fields (stored)
  authType: visualizationAuthTypeSchema.default('none'),
  passwordHash: z.string().optional(), // Stored hash, NEVER returned to client
});

export type VisualizationPublishConfigDTO = z.infer<typeof visualizationPublishConfigDTOSchema>;

// Config for API responses (excludes sensitive fields)
export const visualizationPublishConfigResponseDTOSchema = visualizationPublishConfigDTOSchema.omit(
  {
    passwordHash: true, // Security: passwordHash never leaves the server
  },
);

export type VisualizationPublishConfigResponseDTO = z.infer<
  typeof visualizationPublishConfigResponseDTOSchema
>;

// Password operation discriminated union - makes illegal states unrepresentable
export const visualizationPasswordOperationSchema = z.discriminatedUnion('operation', [
  z.object({ operation: z.literal('keep') }), // Preserve existing (default if omitted)
  z.object({ operation: z.literal('set'), password: z.string().min(1) }), // Set new password
  z.object({ operation: z.literal('remove') }), // Clear password protection
]);

export type VisualizationPasswordOperation = z.infer<typeof visualizationPasswordOperationSchema>;

// Config for upsert requests (input from client)
export const visualizationPublishConfigUpsertDTOSchema = visualizationPublishConfigDTOSchema
  .omit({ authType: true, passwordHash: true }) // Client doesn't set these directly
  .extend({
    // Password management using discriminated union
    passwordOperation: visualizationPasswordOperationSchema.optional(),
  });

export type VisualizationPublishConfigUpsertDTO = z.infer<
  typeof visualizationPublishConfigUpsertDTOSchema
>;

// Published visualization DTO (internal use - includes sensitive config fields)
export const publishedVisualizationDTOSchema = z.object({
  organizationId: z.string().min(1),
  viewId: z.number().int(),
  visualizationId: z.number().int(),
  urlToken: z.string().length(12), // nanoid(12)
  userId: z.string().min(1),
  type: visualizationPublishTypeSchema,
  config: visualizationPublishConfigDTOSchema, // Internal: includes passwordHash
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type PublishedVisualizationDTO = z.infer<typeof publishedVisualizationDTOSchema>;

// Published visualization response DTO (for API responses - excludes sensitive fields)
export const publishedVisualizationResponseDTOSchema = z.object({
  organizationId: z.string().min(1),
  viewId: z.number().int(),
  visualizationId: z.number().int(),
  urlToken: z.string().length(12),
  userId: z.string().min(1),
  type: visualizationPublishTypeSchema,
  config: visualizationPublishConfigResponseDTOSchema, // Security: excludes passwordHash
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type PublishedVisualizationResponseDTO = z.infer<
  typeof publishedVisualizationResponseDTOSchema
>;

// Upsert DTO (for creating or updating a published visualization)
export const publishedVisualizationUpsertDTOSchema = z
  .object({
    type: visualizationPublishTypeSchema,
    config: visualizationPublishConfigUpsertDTOSchema,
  })
  .strict();

export type PublishedVisualizationUpsertDTO = z.infer<typeof publishedVisualizationUpsertDTOSchema>;
